import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { XStack, YStack } from "tamagui";

import PlaybookDetail from "@/components/playbooks/PlaybookDetail";
import type { Playbook } from "@/components/playbooks/types";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchSavedSupportPackSlugs, toggleSavedSupportPack } from "@/lib/savedSupport";
import { HELPER_PACKS } from "@/lib/support-data";
import {
  buildSupportCardLinks,
  getSupportCountryResourceBundle,
  getStaticSupportPlaybook,
  hasLiveSupportForCountry,
  isSupportPlaybookAvailableForCountry,
  isSupportPausedCountry,
  matchesSupportCardCountry,
} from "../../shared/support";
import { getMemberCountryLabel } from "../../shared/location";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

const HelperPack = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [memberCountryCode, setMemberCountryCode] = useState<string | null>(null);

  const pack = HELPER_PACKS.find((item) => item.slug === slug);

  useEffect(() => {
    const fetch = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      const [{ data: seededPlaybook }, { data: cards }, { data: profile }] = await Promise.all([
        supabase
          .from("playbooks")
          .select("*")
          .eq("slug", slug)
          .eq("section", "support")
          .eq("is_active", true)
          .maybeSingle(),
        supabase
          .from("content_cards")
          .select("*")
          .eq("section", "helper-packs")
          .eq("category", slug)
          .eq("is_active", true)
          .order("sort_order", { ascending: true }),
        supabase
          .from("my_private_profile")
          .select("country_code, postcode")
          .maybeSingle(),
      ]);
      const liveSupportAvailable = hasLiveSupportForCountry(profile?.country_code ?? null);
      setMemberCountryCode(profile?.country_code ?? null);
      if (!isSupportPlaybookAvailableForCountry(slug, profile?.country_code ?? null)) {
        setPlaybook(null);
        setLoading(false);
        return;
      }

      const staticPlaybook = getStaticSupportPlaybook(
        slug,
        profile?.country_code ?? null,
        profile?.postcode ?? null,
      );
      if (staticPlaybook) {
        setPlaybook(staticPlaybook as Playbook);
        setLoading(false);
        return;
      }

      if (liveSupportAvailable && seededPlaybook) {
        setPlaybook(seededPlaybook as unknown as Playbook);
        setLoading(false);
        return;
      }

      const visibleCards =
        liveSupportAvailable
          ? (cards ?? []).filter((card) =>
              matchesSupportCardCountry(card, profile?.country_code ?? null),
            ) ?? []
          : [];

      if (pack && visibleCards.length > 0) {
        setPlaybook({
          id: `fallback-${slug}`,
          slug,
          section: "support",
          title: pack.title,
          description: pack.description,
          icon: pack.emoji,
          difficulty: null,
          estimated_time: `${visibleCards.length} steps`,
          life_event_tags: null,
          steps: visibleCards.map((card, index) => ({
            step: index + 1,
            title: card.title,
            description: card.body,
            actions: [],
            links: buildSupportCardLinks(card, {
              countryCode: profile?.country_code ?? null,
              postcode: profile?.postcode ?? null,
            }),
            call_script: null,
            tip: null,
          })),
          is_active: true,
          sort_order: 0,
          last_reviewed_at: new Date().toISOString(),
          review_frequency_months: 6,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      } else {
        setPlaybook(null);
      }

      setLoading(false);
    };

    void fetch();
  }, [slug, pack]);
  const liveSupportAvailable = hasLiveSupportForCountry(memberCountryCode);
  const packAvailable = isSupportPlaybookAvailableForCountry(slug, memberCountryCode);
  const supportPaused = isSupportPausedCountry(memberCountryCode);
  const supportCountryLabel = memberCountryCode ? getMemberCountryLabel(memberCountryCode) : "your country";
  const supportResourceBundle = getSupportCountryResourceBundle(memberCountryCode);

  useEffect(() => {
    if (!slug || !user?.id) {
      setSaved(false);
      return;
    }

    fetchSavedSupportPackSlugs(user.id)
      .then((slugs) => setSaved(slugs.includes(slug)))
      .catch((error) => {
        console.error("Could not load saved support packs:", error);
      });
  }, [slug, user?.id]);

  const handleToggleSaved = async () => {
    if (!slug || !user?.id) return;

    const result = await toggleSavedSupportPack(slug, user.id, saved);

    if (result.error) {
      console.error("Could not update saved support pack:", result.error);
      return;
    }

    setSaved((current) => !current);
  };

  return (
    <>
      <div className="flex flex-wrap gap-3">
        <AppButton type="button" tone="ghost" size="sm" onPress={() => navigate("/support")} alignSelf="flex-start">
          Back to Support
        </AppButton>
        {slug && liveSupportAvailable && packAvailable ? (
          <AppButton type="button" tone="outline" size="sm" onPress={() => void handleToggleSaved()} alignSelf="flex-start">
            {saved ? "Remove saved pack" : "Save for later"}
          </AppButton>
        ) : null}
      </div>

      {loading ? (
        <AppLoadingState
          title="Loading helper pack"
          body="Pulling together the current steps and related guidance for this pack."
        />
      ) : !liveSupportAvailable ? (
        <YStack gap="$sm">
          <AppEmptyState
            title={
              supportPaused
                ? `Support in ${supportCountryLabel} is paused`
                : `Support for ${supportCountryLabel} is coming soon`
            }
            body={
              supportPaused
                ? `${supportCountryLabel} is not on an active Kindred rollout right now. We have paused ${supportCountryLabel} while we put the right public, legal, and support layer in place. We will not show another country's rules as if they apply where you are.`
                : `Kindred Community is ready in ${supportCountryLabel}, and local support guidance for this helper pack is still being built. Start with the local help below while we finish the fuller guidance. We will not show another country's rules as if they apply where you are.`
            }
          />

          <AppCard backgroundColor="$panelSoftBackground" borderColor="$borderStrong">
            <AppCardBody gap="$sm">
              <YStack gap={4}>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  Getting help in {supportCountryLabel}
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  {supportPaused
                    ? `${supportCountryLabel} is paused for now, so these are safer national starting points while Kindred holds back a wider launch until the local legal and support layer is properly ready.`
                    : "Full local Kindred guidance is still coming. These are safer local starting points than relying on UK-only support routes."}
                </AppText>
              </YStack>
              <XStack flexWrap="wrap" gap="$xs">
                {supportResourceBundle.starterLinks.map((link) => (
                  <a key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                    <XStack
                      borderWidth={1}
                      borderColor="$borderStrong"
                      borderRadius="$pill"
                      backgroundColor="$background"
                      paddingHorizontal={12}
                      paddingVertical={8}
                    >
                      <AppText color="$primary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                        {link.label}
                      </AppText>
                    </XStack>
                  </a>
                ))}
              </XStack>
            </AppCardBody>
          </AppCard>

          <AppCard backgroundColor="$accentSoft" borderColor="$accent">
            <AppCardBody gap="$sm">
              <YStack gap={4}>
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  Need support right now?
                </AppText>
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  If today feels unsafe, overwhelming, or too much to hold, start here first.
                </AppText>
              </YStack>
              <XStack flexWrap="wrap" gap="$xs">
                {supportResourceBundle.urgentLinks.map((link) => (
                  <a key={link.href} href={link.href} style={{ textDecoration: "none" }}>
                    <XStack
                      borderWidth={1}
                      borderColor="$accent"
                      borderRadius="$pill"
                      backgroundColor="$background"
                      paddingHorizontal={12}
                      paddingVertical={8}
                    >
                      <AppText color="$secondary" fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                        {link.label}
                      </AppText>
                    </XStack>
                  </a>
                ))}
              </XStack>
            </AppCardBody>
          </AppCard>
        </YStack>
      ) : !playbook ? (
        <AppEmptyState
          title={packAvailable ? "This helper pack is being written" : `This helper pack is not in Kindred's ${supportCountryLabel} library`}
          body={
            packAvailable
              ? "There is no live pack here yet, but the route is ready for when it is published."
              : "Kindred keeps each country on its own sourced support library and only surfaces helper packs backed by the current source set for that country."
          }
        />
      ) : (
        <PlaybookDetail playbook={playbook} />
      )}
    </>
  );
};

export default HelperPack;
